import React from 'react';
import { LoadingBlock } from '../../../../shared/ui/composites';
import { formatPercent } from '../../../../shared/utils/numberLocale';
import { achievementColor } from '../../../../shared/theme/chartPalette';
import { formatReportValue } from '../../utils/reportFormat';
import { cn } from '../../../../lib/utils';
import {
  KPI_COLOR_STYLES,
  normalizeKpiCard,
  normalizeStatConfig,
  evaluateKpiCard,
  kpiSizeClass,
} from '../../utils/kpiItems';

function clampPct(n) {
  if (n == null || !Number.isFinite(Number(n))) return null;
  return Math.min(100, Math.max(0, Number(n)));
}

function Progress({ value, color }) {
  const pct = clampPct(value);
  if (pct == null) return null;
  return (
    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-black/5">
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${pct}%`, backgroundColor: color || achievementColor(pct) }}
      />
    </div>
  );
}

function KpiSubtitleCol({ section, evaluated, align }) {
  if (!section?.label && evaluated?.value == null) {
    return <div />;
  }
  return (
    <div className={cn('min-w-0', align === 'left' ? 'text-left' : 'text-right')}>
      {section?.label ? (
        <p className={cn('text-slate-400', kpiSizeClass(section?.size || 'sm', 'label'))}>
          {section.label}
        </p>
      ) : null}
      {evaluated?.value != null ? (
        <p
          className={cn('font-bold tabular-nums text-slate-600', kpiSizeClass(section?.size || 'sm', 'value'))}
          style={{ color: section?.color || undefined }}
        >
          {formatReportValue(evaluated.value, {
            format: section?.format || 'number',
            decimals: section?.decimals,
            useGrouping: true,
          })}
        </p>
      ) : null}
      <Progress value={evaluated?.progress} color={section?.progressColor} />
    </div>
  );
}

function SectionValue({ section, value, className }) {
  const size = kpiSizeClass(section?.size || 'md', 'value');
  return (
    <span
      className={cn('font-black leading-none tabular-nums tracking-tight', size, className)}
      style={{ color: section?.color || undefined }}
    >
      {formatReportValue(value, {
        format: section?.format || 'number',
        decimals: section?.decimals,
        useGrouping: true,
      })}
    </span>
  );
}

function KpiCardView({ card, row, widgetTitle }) {
  const styles = KPI_COLOR_STYLES[card.color] || KPI_COLOR_STYLES.blue;
  const evaluated = evaluateKpiCard(card, row);
  const showFrame = card.showFrame !== false;
  const showTitle = card.showTitle !== false;
  const padded = card.padded !== false;
  const badgeNum = Number(evaluated.badge.value);
  const badgeColor = Number.isFinite(badgeNum) ? achievementColor(badgeNum) : (card.badge?.color || styles.accent);
  const valueColor = card.value?.color || styles.accent;

  return (
    <div
      className={cn(
        'flex h-full flex-col justify-between',
        card.fitContent ? 'min-h-0' : 'min-h-[7.5rem]',
        padded ? 'p-4' : 'p-0',
        showFrame
          ? cn('rounded-2xl border bg-gradient-to-br shadow-sm', styles.tile, styles.border)
          : 'bg-transparent',
      )}
    >
      {showTitle && (
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-lg', styles.iconBg)}>
              {card.icon || '📊'}
            </span>
            <p className={cn('truncate font-semibold text-slate-500', kpiSizeClass(card.value?.size || 'md', 'label'))}>
              {widgetTitle || 'شاخص'}
            </p>
          </div>
          {(card.badge?.formula || card.badge?.key) && evaluated.badge.value != null && (
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 font-bold text-white',
                kpiSizeClass(card.badge.size || 'sm', 'label'),
              )}
              style={{ backgroundColor: card.badge.color || badgeColor }}
            >
              {card.badge.format === 'percent' || !card.badge.format
                ? formatPercent(Number(evaluated.badge.value) || 0, card.badge.decimals ?? 0)
                : formatReportValue(evaluated.badge.value, card.badge)}
            </span>
          )}
        </div>
      )}

      <div className={cn(showTitle ? 'mt-3' : 'mt-0', 'flex flex-col items-end gap-1 text-right')}>
        <SectionValue
          section={card.value}
          value={evaluated.value.value}
          className="!leading-none"
        />
        <Progress value={evaluated.value.progress} color={card.value?.progressColor || valueColor} />

        {(
          card.subtitleLeft?.label || card.subtitleRight?.label
          || evaluated.subtitleLeft?.value != null || evaluated.subtitleRight?.value != null
        ) && (
          <div className="mt-1 grid w-full grid-cols-2 gap-2">
            <KpiSubtitleCol
              section={card.subtitleLeft}
              evaluated={evaluated.subtitleLeft}
              align="right"
            />
            <KpiSubtitleCol
              section={card.subtitleRight}
              evaluated={evaluated.subtitleRight}
              align="left"
            />
          </div>
        )}
      </div>

      {!showTitle && (card.badge?.formula || card.badge?.key) && evaluated.badge.value != null && (
        <div className="mt-2">
          <Progress value={evaluated.badge.progress} color={card.badge?.progressColor || badgeColor} />
        </div>
      )}

      {evaluated.lines.length > 0 && (
        <div className="mt-3 space-y-1.5 border-t border-black/5 pt-2">
          {evaluated.lines.map((line) => (
            <div key={line.id}>
              <div className="flex items-center justify-between gap-2">
                <span
                  className={cn('truncate text-slate-500', kpiSizeClass(line.size || 'sm', 'label'))}
                  style={{ color: line.color || undefined }}
                >
                  {line.label || line.key || '—'}
                </span>
                <span
                  className={cn('font-bold tabular-nums text-slate-700', kpiSizeClass(line.size || 'sm', 'value'))}
                  style={{ color: line.color || undefined }}
                >
                  {formatReportValue(line.value, {
                    format: line.format || 'number',
                    decimals: line.decimals,
                    useGrouping: true,
                  })}
                </span>
              </div>
              <Progress value={line.progress} color={line.progressColor || line.color} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LegacyGrid({ items, columns, row }) {
  const colCount = Math.max(1, Math.min(4, columns || 3));
  return (
    <div
      className="grid h-auto gap-3 rtl max-md:!grid-cols-1"
      style={{
        gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))`,
        gridAutoFlow: 'row dense',
      }}
    >
      {items.map((item) => {
        const card = {
          icon: item.icon,
          color: item.color,
          showFrame: item.showFrame,
          showTitle: item.showTitle,
          padded: item.padded,
          fitContent: item.fitContent,
          value: {
            key: item.valueKey,
            formula: item.valueFormula,
            format: item.format === 'unit_breakdown' ? 'number' : item.format,
            decimals: item.decimals,
            size: 'lg',
            color: '',
            progressFormula: '',
          },
          subtitleLeft: {
            label: item.subLabel,
            key: item.subKey,
            formula: item.subFormula,
            format: 'number',
            size: 'sm',
          },
          subtitleRight: item.subtitleRight || { label: '', key: '', formula: '', format: 'number', size: 'sm' },
          badge: {
            key: item.badgeKey,
            formula: item.badgeFormula,
            format: item.badgeDisplay === 'number' ? 'number' : 'percent',
            size: 'sm',
          },
          lines: item.lines || [],
        };
        return <KpiCardView key={item.id} card={card} row={row} widgetTitle={item.title} />;
      })}
    </div>
  );
}

export default function StatCardWidget({ widget, data, loading, error }) {
  if (loading) return <LoadingBlock />;
  if (error) return <div className="text-sm text-red-500 p-2">{error}</div>;

  const row = data?.rows?.[0] || {};
  const cfg = widget?.statConfig || {};
  const normalized = normalizeStatConfig(cfg);

  if (Array.isArray(cfg.items) && cfg.items.length > 1) {
    return <LegacyGrid items={normalized.items} columns={normalized.columns} row={row} />;
  }

  const card = normalizeKpiCard(cfg);
  const hasValue = card.value?.formula || card.value?.key || (cfg.items && cfg.items.length);
  const hasSubtitle = card.subtitleLeft?.formula || card.subtitleLeft?.key
    || card.subtitleRight?.formula || card.subtitleRight?.key
    || card.subtitle?.formula;
  if (!hasValue && !(hasSubtitle || card.badge?.formula || card.badge?.key || card.lines?.length)) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-slate-400">
        فیلد مقدار را از کوئری گزارش انتخاب کنید
      </div>
    );
  }

  return <KpiCardView card={card} row={row} widgetTitle={widget?.title} />;
}
